/**
 * Video.js control-bar button that opens one settings panel for everything a
 * viewer can tune locally: subtitles, audio track, quality, subtitle delay and
 * audio delay.
 *
 * Registered as 'SettingsMenuButton' (see registerComponents.ts and
 * controlBarConfig.ts). It replaces the separate captions / audio / quality
 * menus and the two delay buttons, so the control bar stays short on phones.
 *
 * Everything here is per-viewer and local: track and quality choices only touch
 * this player; the delay controllers never broadcast or touch room state.
 *
 * The panel is a child of the player element (so it follows the player into
 * fullscreen) and is positioned with CSS only — see styles/player-skin.css.
 *
 * @packageDocumentation
 */
import videojs from 'video.js';
import type Button from 'video.js/dist/types/button';
import type { Player } from '../useVideoJs';
import { LIMITS } from '@syncreel/shared';
import { createSubtitleDelayController } from '../../lib/subtitleDelay';
import { createAudioDelayController, type AudioDelayController } from '../../lib/audioDelay';
import { listAudioTracks, listTextTracks, selectAudioTrack, selectTextTrack } from '../tracks';

/** Subtitle delay slider step, seconds. */
const SUBTITLE_DELAY_STEP_S = 0.1;
/** Audio delay slider step, milliseconds. */
const AUDIO_DELAY_STEP_MS = 10;

type View = 'main' | 'subtitles' | 'audio' | 'quality' | 'subDelay' | 'audioDelay';

/** Shape of one videojs-contrib-quality-levels entry. */
interface QualityLevel {
  height: number;
  bitrate: number;
  enabled: boolean;
}

/** Shape of the quality-levels list (array-like). */
interface QualityLevelList {
  readonly length: number;
  readonly selectedIndex: number;
  [index: number]: QualityLevel;
}

const VjsButton = videojs.getComponent('Button') as unknown as typeof Button;

/** Create an element with a class and optional text. */
function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** "+1.5 s" / "−0.3 s" / "0.0 s". */
function formatOffset(seconds: number): string {
  const sign = seconds > 0 ? '+' : seconds < 0 ? '−' : '';
  return `${sign}${Math.abs(seconds).toFixed(1)} s`;
}

/** "120 ms". */
function formatDelay(ms: number): string {
  return `${Math.round(ms)} ms`;
}

/**
 * Gear button + settings panel. Registered name: `SettingsMenuButton`.
 */
export class SettingsMenuButton extends VjsButton {
  private readonly subtitleDelay = createSubtitleDelayController(this.player());
  private audioDelay: AudioDelayController | null = null;

  private panelEl: HTMLDivElement | null = null;
  private view: View = 'main';
  private open = false;

  constructor(player: Player, options?: Record<string, unknown>) {
    super(player, options);
    this.controlText('Settings');

    const panel = el('div', 'vjs-settings-panel');
    panel.setAttribute('role', 'menu');
    panel.setAttribute('aria-label', 'Settings');
    panel.setAttribute('aria-hidden', 'true');
    this.player().el().appendChild(panel);
    this.panelEl = panel;
  }

  override buildCSSClass(): string {
    return `vjs-settings-button vjs-icon-cog ${super.buildCSSClass()}`;
  }

  override handleClick(): void {
    if (this.open) this.close();
    else this.openPanel();
  }

  // ── Data sources ─────────────────────────────────────────────────────────

  private qualityLevels(): QualityLevelList | null {
    const p = this.player() as unknown as { qualityLevels?: () => QualityLevelList };
    try {
      return p.qualityLevels ? p.qualityLevels() : null;
    } catch {
      return null;
    }
  }

  /** Distinct rendition heights, tallest first. */
  private qualityHeights(): number[] {
    const list = this.qualityLevels();
    if (!list) return [];
    const heights = new Set<number>();
    for (let i = 0; i < list.length; i += 1) {
      const h = list[i]?.height;
      if (typeof h === 'number' && h > 0) heights.add(h);
    }
    return [...heights].sort((a, b) => b - a);
  }

  /** The pinned height, or null while quality is automatic. */
  private pinnedHeight(): number | null {
    const list = this.qualityLevels();
    if (!list || list.length === 0) return null;
    const enabled: QualityLevel[] = [];
    for (let i = 0; i < list.length; i += 1) {
      const lvl = list[i];
      if (lvl?.enabled) enabled.push(lvl);
    }
    if (enabled.length === 0 || enabled.length === list.length) return null;
    return enabled[0]?.height ?? null;
  }

  private setQuality(height: number | null): void {
    const list = this.qualityLevels();
    if (!list) return;
    for (let i = 0; i < list.length; i += 1) {
      const lvl = list[i];
      if (lvl) lvl.enabled = height === null || lvl.height === height;
    }
  }

  private ensureAudioDelay(): AudioDelayController | null {
    if (this.audioDelay) return this.audioDelay;
    const video = this.player().el().querySelector('video');
    if (!video) return null;
    this.audioDelay = createAudioDelayController(video as HTMLVideoElement);
    return this.audioDelay;
  }

  private subtitleTracks() {
    return listTextTracks(this.player()).filter((t) => t.kind === 'subtitles' || t.kind === 'captions');
  }

  // ── Open / close ─────────────────────────────────────────────────────────

  private openPanel(): void {
    if (!this.panelEl) return;
    this.view = 'main';
    this.open = true;
    this.render();
    this.panelEl.setAttribute('aria-hidden', 'false');
    this.player().addClass('vjs-settings-open');
    this.addClass('vjs-settings-active');
    // WHY: listeners are added after the opening click has bubbled so it does
    // not immediately close the panel it just opened.
    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('keydown', this.onDocumentKeydown);
  }

  private close(): void {
    if (!this.panelEl) return;
    this.open = false;
    this.panelEl.setAttribute('aria-hidden', 'true');
    this.player().removeClass('vjs-settings-open');
    this.removeClass('vjs-settings-active');
    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  }

  private readonly onDocumentClick = (event: MouseEvent): void => {
    const target = event.target as Node | null;
    if (!target) return;
    if (this.panelEl?.contains(target) || this.el().contains(target)) return;
    this.close();
  };

  private readonly onDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    if (this.view !== 'main') this.go('main');
    else this.close();
  };

  private go(view: View): void {
    this.view = view;
    this.render();
  }

  // ── Rendering ────────────────────────────────────────────────────────────

  private render(): void {
    const panel = this.panelEl;
    if (!panel) return;
    panel.replaceChildren();
    panel.dataset.view = this.view;

    const titles: Record<View, string> = {
      main: 'Settings',
      subtitles: 'Subtitles',
      audio: 'Audio',
      quality: 'Quality',
      subDelay: 'Subtitle delay',
      audioDelay: 'Audio delay',
    };

    const head = el('div', 'vjs-settings-head');
    if (this.view !== 'main') {
      const back = el('button', 'vjs-settings-back');
      back.type = 'button';
      back.setAttribute('aria-label', 'Back to settings');
      back.addEventListener('click', () => this.go('main'));
      head.appendChild(back);
    }
    head.appendChild(el('span', 'vjs-settings-title', titles[this.view]));
    panel.appendChild(head);

    const body = el('div', 'vjs-settings-body');
    switch (this.view) {
      case 'main':
        this.renderMain(body);
        break;
      case 'subtitles':
        this.renderSubtitles(body);
        break;
      case 'audio':
        this.renderAudio(body);
        break;
      case 'quality':
        this.renderQuality(body);
        break;
      case 'subDelay':
        this.renderSubtitleDelay(body);
        break;
      case 'audioDelay':
        this.renderAudioDelay(body);
        break;
    }
    panel.appendChild(body);
  }

  private row(body: HTMLElement, kind: string, label: string, value: string, target: View): void {
    const btn = el('button', 'vjs-settings-row');
    btn.type = 'button';
    btn.dataset.row = kind;
    btn.setAttribute('role', 'menuitem');
    btn.appendChild(el('span', 'vjs-settings-row-label', label));
    btn.appendChild(el('span', 'vjs-settings-row-value', value));
    btn.addEventListener('click', () => this.go(target));
    body.appendChild(btn);
  }

  private renderMain(body: HTMLElement): void {
    const subs = this.subtitleTracks();
    if (subs.length > 0) {
      const active = subs.find((t) => t.mode === 'showing');
      this.row(body, 'subtitles', 'Subtitles', active ? active.label || active.language || 'On' : 'Off', 'subtitles');
    }
    const audio = listAudioTracks(this.player());
    if (audio.length > 1) {
      const active = audio.find((t) => t.enabled);
      this.row(body, 'audio', 'Audio', active ? active.label || active.language || 'Default' : 'Default', 'audio');
    }
    if (this.qualityHeights().length > 1) {
      const pinned = this.pinnedHeight();
      this.row(body, 'quality', 'Quality', pinned === null ? 'Auto' : `${pinned}p`, 'quality');
    }
    this.row(body, 'subDelay', 'Subtitle delay', formatOffset(this.subtitleDelay.getOffset()), 'subDelay');
    this.row(body, 'audioDelay', 'Audio delay', formatDelay(this.ensureAudioDelay()?.getDelayMs() ?? 0), 'audioDelay');
  }

  private option(body: HTMLElement, label: string, selected: boolean, onPick: () => void): void {
    const btn = el('button', `vjs-settings-option${selected ? ' vjs-selected' : ''}`);
    btn.type = 'button';
    btn.setAttribute('role', 'menuitemradio');
    btn.setAttribute('aria-checked', String(selected));
    btn.appendChild(el('span', 'vjs-settings-option-label', label));
    btn.addEventListener('click', onPick);
    body.appendChild(btn);
  }

  private renderSubtitles(body: HTMLElement): void {
    const subs = this.subtitleTracks();
    const anyOn = subs.some((t) => t.mode === 'showing');
    this.option(body, 'Off', !anyOn, () => {
      selectTextTrack(this.player(), null);
      this.close();
    });
    for (const t of subs) {
      this.option(body, t.label || t.language || 'Track', t.mode === 'showing', () => {
        selectTextTrack(this.player(), t.id);
        this.close();
      });
    }
  }

  private renderAudio(body: HTMLElement): void {
    for (const t of listAudioTracks(this.player())) {
      this.option(body, t.label || t.language || 'Track', t.enabled, () => {
        selectAudioTrack(this.player(), t.id);
        this.close();
      });
    }
  }

  private renderQuality(body: HTMLElement): void {
    const pinned = this.pinnedHeight();
    this.option(body, 'Auto', pinned === null, () => {
      this.setQuality(null);
      this.close();
    });
    for (const h of this.qualityHeights()) {
      this.option(body, `${h}p`, pinned === h, () => {
        this.setQuality(h);
        this.close();
      });
    }
  }

  /** Slider + readout + reset, shared by both delay views. */
  private slider(
    body: HTMLElement,
    cfg: { min: number; max: number; step: number; value: number; format: (n: number) => string; set: (n: number) => void; reset: () => void },
  ): HTMLInputElement {
    const row = el('div', 'vjs-settings-slider-row');
    const input = el('input', 'vjs-settings-slider');
    input.type = 'range';
    input.min = String(cfg.min);
    input.max = String(cfg.max);
    input.step = String(cfg.step);
    input.value = String(cfg.value);
    const readout = el('span', 'vjs-settings-readout', cfg.format(cfg.value));
    input.addEventListener('input', () => {
      const n = Number(input.value);
      if (Number.isNaN(n)) return;
      cfg.set(n);
      readout.textContent = cfg.format(n);
    });
    row.appendChild(input);
    row.appendChild(readout);
    body.appendChild(row);

    const reset = el('button', 'vjs-settings-reset', 'Reset');
    reset.type = 'button';
    reset.addEventListener('click', () => {
      cfg.reset();
      input.value = String(cfg.min <= 0 && cfg.max >= 0 ? 0 : cfg.min);
      readout.textContent = cfg.format(Number(input.value));
    });
    body.appendChild(reset);
    return input;
  }

  private renderSubtitleDelay(body: HTMLElement): void {
    this.slider(body, {
      min: -LIMITS.SUBTITLE_OFFSET_RANGE_S,
      max: LIMITS.SUBTITLE_OFFSET_RANGE_S,
      step: SUBTITLE_DELAY_STEP_S,
      value: this.subtitleDelay.getOffset(),
      format: formatOffset,
      set: (n) => this.subtitleDelay.setOffset(n),
      reset: () => this.subtitleDelay.reset(),
    });
  }

  private renderAudioDelay(body: HTMLElement): void {
    const controller = this.ensureAudioDelay();
    const available = controller ? controller.isAvailable() : false;
    const input = this.slider(body, {
      min: 0,
      max: LIMITS.AUDIO_DELAY_MAX_MS,
      step: AUDIO_DELAY_STEP_MS,
      value: controller?.getDelayMs() ?? 0,
      format: formatDelay,
      // setDelayMs is async (it may resume the AudioContext); fire and forget.
      set: (n) => void this.ensureAudioDelay()?.setDelayMs(n),
      reset: () => void this.ensureAudioDelay()?.setDelayMs(0),
    });
    input.disabled = !available;
    const note = el(
      'p',
      'vjs-settings-note',
      available
        ? 'Audio can only be delayed, not advanced. To fix audio that is late, use subtitle delay or ask the host to re-sync.'
        : 'Audio delay is unavailable on this browser or media.',
    );
    body.appendChild(note);
  }

  // ── Teardown ─────────────────────────────────────────────────────────────

  override dispose(): void {
    this.close();
    this.subtitleDelay.reset();
    this.audioDelay?.dispose();
    this.audioDelay = null;
    this.panelEl?.remove();
    this.panelEl = null;
    super.dispose();
  }
}
